import { Button } from '@/components/UI/Common/Admin/Button';
import { Badge } from '@/components/UI/Common/Admin/Badge';
import { Checkbox } from '@/components/UI/Common/Admin/Checkbox';
import { LoadingSpinner } from '@/components/UI/Common/Admin/LoadingSpinner';
import { PageLayout } from '@/components/UI/Layouts/Admin/PageLayout';
import { Pagination } from '@/components/UI/Pagination/Pagination';
import Table from '@/components/UI/Table/Table';
import { ROUTES } from '@/config/routes';
import { useResourceList } from '@/hooks/useResourceList';
import { useDeleteMany, useGetList } from '@/queries/admin';
import { IApplication } from '@/types/appliction';
import { IUserGroup } from '@/types/groups';
import { TableColumn } from '@/types/table';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { Filters } from '../components/Filters';

export const ApplicationsPage = () => {
  const {
    data: applications,
    isLoading,
    reset,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleLimitChange,
    filter,
    page,
    sortOrder,
    totalPages,
    totalItems,
    limit,
    sortBy,
  } = useResourceList('applications');
  const { data: applicationGroups } = useGetList('application-groups');
  const { mutateAsync: deleteApplications } = useDeleteMany('applications');
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const navigate = useNavigate();

  const toggleDelete = () => {
    if (idsToDelete?.length === applications?.data.length) {
      setIdsToDelete([]);
    } else {
      setIdsToDelete(applications?.data.map((app) => app.id) || []);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApplications({ ids: idsToDelete });
      toast.success('Приложения успешно удалены');
      setIdsToDelete([]);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Произошла ошибка при удалении приложений');
    }
  };

  const renderCheckBox = (_val: unknown, row: IApplication) => (
    <Checkbox
      className="prevent-row-click"
      checked={idsToDelete?.includes(row.id)}
      onChange={() => {
        if (idsToDelete?.includes(row.id)) {
          setIdsToDelete(idsToDelete.filter((id) => id !== row.id));
        } else {
          setIdsToDelete([...idsToDelete, row.id]);
        }
      }}
    />
  );

  const columns: TableColumn<IApplication>[] = useMemo(
    () => [
      {
        key: '',
        title: (
          <Checkbox
            onClick={toggleDelete}
            onChange={() => {}}
            checked={idsToDelete?.length === applications?.data.length}
          />
        ),
        render: renderCheckBox,
      },
      {
        key: 'id',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        title: 'ID',
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'title',
        title: 'Название',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      { key: 'description', title: 'Описание' },
      { key: 'link', title: 'Ссылка' },
      { key: 'deviceType', title: 'Тип устройства' },
      {
        key: 'userGroups',
        title: 'Группы пользователей',
        render: (val: unknown) => {
          const groups = val as IUserGroup[];
          return groups?.length ? (
            <div className="flex flex-wrap gap-1">
              {groups.map((group) => (
                <Badge key={group.id} variant="info">
                  {group.name}
                </Badge>
              ))}
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        key: 'sort',
        title: 'Порядок',
        sortable: true,
        sortBy: sortBy,
        sortOrder: sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'applicationGroupId',
        title: 'Группа приложения',
        render: (val: unknown) => applicationGroups?.data.find((group) => group.id === val)?.title,
      },
      {
        key: 'createdAt',
        title: 'Создано',
        sortable: true,
        sortBy: sortBy,
        onColumnHeaderClick: handleSortChange,
        sortOrder: sortOrder,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        key: 'updatedAt',
        title: 'Обновлено',
        sortable: true,
        sortBy: sortBy,
        onColumnHeaderClick: handleSortChange,
        sortOrder: sortOrder,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    [applicationGroups?.data, idsToDelete.length, applications?.data.length, sortBy, sortOrder],
  );

  if (isLoading || !applications) {
    return <LoadingSpinner className="h-full" text="Загрузка..." />;
  }

  return (
    <PageLayout
      actions={
        <div className="flex gap-4">
          {idsToDelete.length > 0 && (
            <Button onClick={handleDelete} size="md" variant="danger">
              Удалить
            </Button>
          )}
          <Link to={ROUTES.ADMIN.CREATE('applications')}>
            <Button size="md" variant="success">
              Создать
            </Button>
          </Link>
        </div>
      }
      title="Приложения"
    >
      <Filters value={filter} onChange={handleFilterChange} onReset={reset} />

      {applications.data.length > 0 && (
        <div className="overflow-x-auto">
          <Table
            data={applications!.data}
            columns={columns}
            onRowClick={(row) => navigate(ROUTES.ADMIN.EDIT('applications', row.id))}
          />
        </div>
      )}

      <Pagination
        className="mt-10"
        onItemsPerPageChange={handleLimitChange}
        currentPage={page}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={limit}
      />
    </PageLayout>
  );
};
